<template>
  <div
      class="smart-input-tag"
      :class="{ focused: isFocused }"
      @mousedown.prevent="focusInput"
  >
    <!-- 标签行 -->
    <div
        class="tag-list"
        :class="{ ellipsis: !isFocused }"
        v-if="tags && tags.length > 0"
    >
      <a-tag
          v-for="(tag, index) in tags"
          :key="index"
          closable
          @mousedown.stop
          @close.stop="removeTag(index)"
          class="tag-item"
      >
        {{ tag }}
      </a-tag>
    </div>

    <!-- 未聚焦且无标签时：显示 placeholder -->
    <div v-else-if="!isFocused" class="placeholder">
      {{ lang(props.placeholder) }}
    </div>

    <!-- 输入框：只有聚焦时显示 -->
    <a-input
        v-if="isFocused && tags.length < props.limit"
        v-model:value="inputValue"
        class="tag-input"
        :placeholder="lang(props.placeholder)"
        @keydown.enter.prevent="addTag"
        @keydown.space.prevent="addTag"
        @keydown.comma.prevent="addTag"
        @paste="handlePaste"
        @focusout="handleFocusOut"
        ref="inputRef"
    />

    <!-- 清空按钮 -->
    <a-button
        v-if="isFocused && tags.length"
        type="text"
        size="small"
        @mousedown.stop
        @click.stop="clearAll"
        class="clear-btn"
    >
      {{ lang('清空') }}
    </a-button>
  </div>
</template>

<script setup lang="tsx" name="SmartInputTag">
import {inject, nextTick, ref, watch} from 'vue'

const lang = inject('lang')

const props = defineProps({
  value: {
    type: Array as () => string[],
    default: () => []
  },
  placeholder: {
    type: String,
    default: '请输入'
  },
  limit: {
    type: Number,
    default: 100
  }
})

const emit = defineEmits(['update:value'])

const tags = ref<string[]>([...props.value])
const inputValue = ref('')
const isFocused = ref(false)
const inputRef = ref<any>(null)

// 外部 v-model:value 改变时，同步内部 tags
watch(
    () => props.value,
    (val) => {
      tags.value = Array.isArray(val) ? [...val] : []
    }
)

// 更新方法（去重 + 限制数量）
const updateTags = (newTags: string[]) => {
  const unique = Array.from(new Set(newTags.map(t => String(t).trim()).filter(Boolean)))
  tags.value = unique.slice(0, props.limit)
  emit('update:value', tags.value)
}

// 添加标签
const addTag = () => {
  const val = inputValue.value.trim()
  if (!val) return
  updateTags([...tags.value, val])
  inputValue.value = ''
}

// 删除标签（不再强制 focus，避免与 mousedown.prevent 冲突）
const removeTag = (index: number) => {
  const newTags = [...tags.value]
  newTags.splice(index, 1)
  updateTags(newTags)
  // 如果你希望在删除时保持输入框聚焦，可以恢复下面的代码：
  // nextTick(() => { inputRef.value?.focus(); isFocused.value = true })
}

// 清空
const clearAll = () => {
  updateTags([])
  // 保留聚焦行为（若需要），也可以把 isFocused = false
  nextTick(() => {
    inputRef.value?.focus()
    isFocused.value = true
  })
}

// 粘贴处理
const handlePaste = (e: ClipboardEvent) => {
  e.preventDefault()
  const pastedText = e.clipboardData?.getData('text') || ''
  if (!pastedText) return

  const items = pastedText
      .split(/[\n,，, ]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

  if (items.length) {
    updateTags([...tags.value, ...items])
  }
}

// 使用 focusout（支持 relatedTarget）判断焦点是否移出整个组件
const handleFocusOut = (e: FocusEvent) => {
  // relatedTarget 指向将要获得焦点的元素（可能为 null）
  const related = (e as any).relatedTarget as HTMLElement | null
  if (related && related.closest && related.closest('.smart-input-tag')) {
    // 焦点移到本组件内的其他元素，忽略（不收起）
    return
  }

  // 真正移出组件时：把剩余输入转成 tag，并收起
  if (inputValue.value.trim()) {
    addTag()
  }
  isFocused.value = false
}

// 点击/按下容器 → 聚焦（使用 mousedown.prevent 保证顺序稳定）
const focusInput = () => {
  isFocused.value = true
  nextTick(() => {
    inputRef.value?.focus?.()
  })
}
</script>

<style scoped>
.smart-input-tag {
  width: 300px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 4px 8px;
  min-height: 32px;
  cursor: text;
  transition: all 0.2s;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  padding-right: 20px;
}

.smart-input-tag.focused {
  --actions-width: 60px; /* 给右侧清空按钮预留宽度 */
  padding-right: var(--actions-width); /* 避免标签覆盖按钮 */
  padding-bottom: 6px;
  flex-direction: column;
  align-items: flex-start;
}

/* 标签行 */
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
}

/* 未聚焦时：单行省略 */
.tag-list.ellipsis {
  flex-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 聚焦时：最多展示 5 行，多余的滚动 */
.smart-input-tag.focused .tag-list {
  max-height: calc(24px * 5 + 4px * 4); /* 5行高度 + 间距 */
  overflow-y: auto;
  width: 100%;
}

/* placeholder 样式 */
.placeholder {
  color: #bfbfbf;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 输入框 */
.tag-input {
  margin-top: 4px;
  width: 100%;
}

/* 清空按钮固定在右上角 */
.clear-btn {
  position: absolute;
  top: 4px;
  right: 8px;
  font-size: 12px;
  color: #999;
  z-index: 10;
  background: #fff; /* 防止被标签遮住 */
}
</style>
